import api from 'src/config/api';
import { getJsonResponse } from 'src/utils/ApiUtils';
import Deal, { LegacyDeal } from 'src/interfaces/api/Deal';
import useAuth from './useAuth';

const useDeal = () => {
  const { fetch } = useAuth();

  const get = async (id: string): Promise<Deal> => {
    const { endpoint, ...options } = api.deal.get(id);

    const response = await fetch(endpoint, options);

    try {
      return await getJsonResponse<Deal>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const getLegacy = async (id: number, fields = ''): Promise<LegacyDeal> => {
    const { endpoint, ...options } = api.deal.getLegacy(id, fields);

    const response = await fetch(endpoint, options);

    try {
      const json = await getJsonResponse<{ data: { deal: LegacyDeal } }>(response);

      return json.data.deal;
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const getDeliveryOrderToken = async (id: number, providerId: string): Promise<string> => {
    const { endpoint, ...options } = api.deal.getDeliveryOrderToken(id, providerId);

    const response = await fetch(endpoint, options);

    try {
      const data = await getJsonResponse<{ token: string }>(response);

      return data.token;
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const search = async (
    criteria: Record<string, unknown>,
    page: number,
    limit: number,
    orderBy = 'createdAt',
    order: 'asc' | 'desc' = 'asc'
  ): Promise<LegacyDeal[]> => {
    const { endpoint, ...options } = api.deal.search(criteria, page, limit, orderBy, order);

    const response = await fetch(endpoint, options);

    try {
      const json = await getJsonResponse<{ data: { deals: { items: LegacyDeal[] } }; errors?: { message?: string } }>(
        response
      );

      if (json.errors) {
        throw new Error(json.errors.message || 'Erreur lors de la récupèration des affaires');
      }

      return json.data.deals.items;
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const getEmployeesDealAverageRatings = async (
    dateStart?: string,
    dateEnd?: string
  ): Promise<{ employeeId: string; ratingCount: number; averageRating: number }[]> => {
    const { endpoint, ...options } = api.deal.getEmployeeDealAverageRatings(dateStart, dateEnd);

    const response = await fetch(endpoint, options);

    try {
      return await getJsonResponse<{ employeeId: string; ratingCount: number; averageRating: number }[]>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  return { get, getLegacy, search, getDeliveryOrderToken, getEmployeesDealAverageRatings };
};

export default useDeal;
